/* The last net under the whole tree, mounted in `index.tsx` around `Providers`.

   React unmounts everything below a component that throws, so without this a
   render crash is a white screen — the one failure mode that tells the user
   nothing at all (00-PLAN.md, the no-silent rule). A class component because
   `componentDidCatch` still has no hook equivalent.

   `console.error` always: a boundary that renders a card and eats the error
   would just be a prettier silence. The stack shows outside production only —
   a stack frame can name internal paths. */
import React from "react";

interface Props {
  children: React.ReactNode;
}

interface State {
  error: Error | null;
  componentStack: string | null;
}

const SHOW_STACK = process.env.NODE_ENV !== "production";

export class ErrorBoundary extends React.Component<Props, State> {
  state: State = { error: null, componentStack: null };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    console.error(error, info);
    this.setState({ componentStack: info.componentStack ?? null });
  }

  render(): React.ReactNode {
    const { error, componentStack } = this.state;
    if (!error) return this.props.children;

    return (
      <div className="h-screen w-screen bg-[var(--surface-sunk)] flex items-center justify-center p-6">
        <div className="w-[520px] max-w-full rounded-[14px] bg-[var(--surface)] ring-1 ring-[var(--hairline)] p-4 fade-in">
          <div className="text-[14px] font-medium tracking-[-0.005em]">Something broke</div>
          <div className="text-[12px] text-[var(--text-tertiary)] mt-1 leading-[1.55]">
            {error.message || "This screen stopped rendering."}
          </div>
          {SHOW_STACK && (
            <pre className="mt-3 max-h-[42vh] overflow-auto rounded-[10px] bg-[var(--surface-2)] p-3 font-mono text-[11px] leading-[1.5] text-[var(--text-secondary)] whitespace-pre-wrap">
              {error.stack}
              {componentStack}
            </pre>
          )}
          <button
            onClick={() => window.location.reload()}
            className="press mt-4 h-8 px-3.5 rounded-[10px] bg-[var(--brand)] shadow-button text-[var(--brand-foreground)] text-[12px] font-medium hover:bg-[var(--brand-hover)] flex items-center gap-1.5"
          >
            Reload
          </button>
        </div>
      </div>
    );
  }
}
